import { z } from "zod/v3";
import {
	EXPENSE_TYPE_CODES,
	type ExpenseTypeCode,
	MAINTENANCE_TYPE_CODES,
} from "../types";

const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;

const baseExpenseSchema = z.object({
	type: z
		.number({ message: "Selecione o tipo de despesa" })
		.refine(
			(val) =>
				Object.values(EXPENSE_TYPE_CODES).includes(val as ExpenseTypeCode),
			{
				message: "Selecione o tipo de despesa",
			}
		),
	vehicleId: z.string().min(1, "Selecione o veículo"),
	amount: z
		.number({ message: "Informe o valor" })
		.positive("O valor deve ser maior que zero"),
	date: z
		.string()
		.min(1, "Informe a data")
		.regex(isoDateRegex, "Data deve estar no formato aaaa-mm-dd"),
	description: z.string().max(500).optional().or(z.literal("")),
	attachment: z.instanceof(File).optional().or(z.literal(undefined)),
	infractionNumber: z.string().max(15).optional().or(z.literal("")),
	infractionDueDate: z.string().optional().or(z.literal("")),
	driverIdentificationDueDate: z.string().optional().or(z.literal("")),
	maintenanceType: z
		.union([
			z.literal(MAINTENANCE_TYPE_CODES.Preventive),
			z.literal(MAINTENANCE_TYPE_CODES.Corrective),
		])
		.optional(),
	fuelLiters: z.number().min(0).optional(),
	reimbursementReason: z.number().optional(),
});

type SchemaData = z.infer<typeof baseExpenseSchema>;

type RefineCtx = { addIssue: (issue: z.ZodIssue) => void };

function addInfractionRefine(data: SchemaData, ctx: RefineCtx) {
	if (data.type !== EXPENSE_TYPE_CODES.Infraction) {
		return;
	}
	if (!data.infractionNumber?.trim()) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: "Informe o código do auto",
			path: ["infractionNumber"],
		});
	}

	if (data.infractionDueDate && !isoDateRegex.test(data.infractionDueDate)) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: "Data de vencimento inválida",
			path: ["infractionDueDate"],
		});
	}
	const driverIdentificationDueDateTrimmed =
		data.driverIdentificationDueDate?.trim();

	if (!driverIdentificationDueDateTrimmed) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: "Informe a data limite condutor",
			path: ["driverIdentificationDueDate"],
		});
	} else if (!isoDateRegex.test(driverIdentificationDueDateTrimmed)) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: "Data limite condutor inválida",
			path: ["driverIdentificationDueDate"],
		});
	}
}

function addMaintenanceRefine(data: SchemaData, ctx: RefineCtx) {
	if (
		data.type !== EXPENSE_TYPE_CODES.Maintenance ||
		(data.maintenanceType !== undefined && data.maintenanceType !== null)
	) {
		return;
	}
	ctx.addIssue({
		code: z.ZodIssueCode.custom,
		message: "Selecione o tipo de manutenção",
		path: ["maintenanceType"],
	});
}

function addFuelRefine(data: SchemaData, ctx: RefineCtx) {
	if (
		data.type !== EXPENSE_TYPE_CODES.Fuel ||
		(data.fuelLiters !== undefined &&
			data.fuelLiters !== null &&
			data.fuelLiters > 0)
	) {
		return;
	}
	ctx.addIssue({
		code: z.ZodIssueCode.custom,
		message: "Informe a quantidade em litros",
		path: ["fuelLiters"],
	});
}

function addReimbursementRefine(data: SchemaData, ctx: RefineCtx) {
	if (
		data.type !== EXPENSE_TYPE_CODES.Reimbursement ||
		(data.reimbursementReason !== undefined &&
			data.reimbursementReason !== null)
	) {
		return;
	}
	ctx.addIssue({
		code: z.ZodIssueCode.custom,
		message: "Informe o motivo do reembolso",
		path: ["reimbursementReason"],
	});
}

function addOtherRefine(data: SchemaData, ctx: RefineCtx) {
	if (data.type !== EXPENSE_TYPE_CODES.Other) {
		return;
	}

	if (!data.description?.trim()) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: "Informe a observação",
			path: ["description"],
		});
	}
}

export const newExpenseSchema = baseExpenseSchema.superRefine((data, ctx) => {
	addInfractionRefine(data, ctx);
	addMaintenanceRefine(data, ctx);
	addFuelRefine(data, ctx);
	addReimbursementRefine(data, ctx);
	addOtherRefine(data, ctx);
});

export type NewExpenseSchema = z.infer<typeof newExpenseSchema>;
